import { useState, useEffect, type MouseEvent, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { type ControlPosition } from 'maplibre-gl'
import { useControl } from 'react-map-gl/maplibre'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { getParkingLocationColor, getRouteColor } from 'screens/TripDetails/TripDetails.helpers'
import { KNGenericMapIcon, KNMapRouteIcon } from 'components/KNIcon/KNMapIcon'
import { BaseControl } from './helpers'
import { MapLayerType, type MapLayers } from './types'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import { ParkingLocationType } from 'screens/TripDetails/TripDetails.types'

const getMapLayerIcon = (type: MapLayerType): ReactElement => {
  switch (type) {
    case MapLayerType.TRACKED_ROUTES:
      return <KNMapRouteIcon color={getRouteColor('vehicle_fast')} />
    case MapLayerType.SHORTEST_ROUTES:
      return <KNMapRouteIcon color={getRouteColor('shortest_route')} />
    case MapLayerType.PARKING_LOCATIONS:
      return <KNGenericMapIcon size={24} color={getParkingLocationColor(ParkingLocationType.BASIC_SECURITY)}
                               label="P" />
  }
}

type LayersButtonProps = {
  activeLayers: MapLayers
  onClick: (layer: MapLayerType) => void
}

type LayersControlProps = {
  position?: ControlPosition
  activeLayers: MapLayers
  onClick: (layer: MapLayerType) => void
}

const LayersButton = ({
                        activeLayers,
                        onClick,
                      }: LayersButtonProps): ReactElement => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // using basic <button> element with maplibre's classes to keep styling consistent with other controls
  return (
    <>
      <button type="button" className="maplibregl-ctrl-layers" onClick={handleOpen}>
        <span className="maplibregl-ctrl-icon" />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: -8,
        }}
        slotProps={{
          list: {
            'dense': true,
          },
        }}
      >
        {Object.values(MapLayerType).map((layer) => (
          <MenuItem
            key={layer}
            selected={activeLayers[layer]}
            onClick={() => onClick(layer)}
          >
            <ListItemIcon>
              {getMapLayerIcon(layer)}
            </ListItemIcon>
            <ListItemText disableTypography primary={t(`screens.cs.trip_details.map.layers.${layer}`)} />
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

const KNMapLibreLayersControl = ({
                                   position,
                                   activeLayers,
                                   onClick,
                                 }: LayersControlProps): null => {
  const control = useControl<BaseControl>(() => new BaseControl(), { position })

  useEffect(() => {
    control.getRoot()?.render(<LayersButton activeLayers={activeLayers} onClick={onClick} />)
  }, [control, activeLayers, onClick])

  return null
}

export default KNMapLibreLayersControl
